import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Newspaper,
  Inbox,
  AlertTriangle,
  Activity,
  Eye,
  Heart,
  Flag,
  Radio,
  Building2,
  Users,
  FileStack,
  Share2,
  BookOpen,
  TrendingUp,
  TrendingDown,
  Minus,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import { Button } from '@/components/ui/button';
import {
  MEDIA_CLASS_BADGE,
  MEDIA_SENTIMENT_BADGE,
  MEDIA_STATUS_BADGE,
  MEDIUM_LABEL,
  PLATFORM_LABEL,
  publicMetrics,
  type CoverageItem,
  type MediaSentiment,
  type SocialPlatform,
  type TraditionalMedium,
} from '@/client/types/media';
import { useListeningRulesQuery } from '@/client/api/marketing/sources';
import { MediaPageShell } from './components/MediaPageShell';
import { PlatformLogoBadge } from './components/PlatformLogo';
import { OutletFavicon } from './components/OutletFavicon';
import { formatCompact, sourceLabel } from './fixtures';
import { matchingKeywords } from './keyword-match';
import {
  publishedRangeParams,
  useAdaptedAccounts,
  useAdaptedOutlets,
  useApiTrackedKeywords,
  useCoverageItems,
} from './coverage-api';
import { ListeningHealthStrip } from './ListeningHealthStrip';
import { useBriefsQuery, usePerformanceTrackedQuery } from '@/client/api/marketing/radar';

type RangeKey = '7d' | '30d' | '90d' | 'all';

const SENTIMENT_COLORS: Record<MediaSentiment, string> = {
  positive: '#059669',
  neutral: '#6b7280',
  negative: '#e11d48',
  unknown: '#d97706',
};

const CHANNEL_COLORS = {
  social: '#2563eb',
  traditional: '#7c3aed',
};

function rangeDays(range: RangeKey): number | 'all' {
  if (range === '7d') return 7;
  if (range === '30d') return 30;
  if (range === '90d') return 90;
  return 'all';
}

function avg(nums: number[]) {
  if (nums.length === 0) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function healthFromSignals(opts: {
  flagged: number;
  unreviewed: number;
  negativeShare: number;
  avgUrgency: number;
}) {
  let score = 100;
  score -= Math.min(40, opts.flagged * 12);
  score -= Math.min(25, opts.unreviewed * 3);
  score -= Math.min(25, opts.negativeShare * 40);
  score -= Math.min(15, Math.max(0, opts.avgUrgency - 40) / 4);
  score = Math.round(Math.max(0, Math.min(100, score)));

  if (opts.flagged >= 2 || opts.negativeShare >= 0.35 || score < 45) {
    return { score, level: 'critical' as const, label: 'Needs attention' };
  }
  if (opts.flagged >= 1 || opts.unreviewed >= 5 || opts.negativeShare >= 0.2 || score < 70) {
    return { score, level: 'watch' as const, label: 'Watch closely' };
  }
  return { score, level: 'healthy' as const, label: 'Healthy' };
}

function Kpi({
  label,
  value,
  hint,
  icon: Icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  tone?: 'default' | 'danger' | 'warn' | 'good';
}) {
  const toneCls =
    tone === 'danger'
      ? 'border-red-100 bg-red-50/60'
      : tone === 'warn'
        ? 'border-amber-100 bg-amber-50/60'
        : tone === 'good'
          ? 'border-emerald-100 bg-emerald-50/40'
          : 'border-gray-100 bg-white';
  const iconCls =
    tone === 'danger'
      ? 'bg-red-100 text-red-700'
      : tone === 'warn'
        ? 'bg-amber-100 text-amber-800'
        : tone === 'good'
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-gray-50 text-gray-600';

  return (
    <div className={`rounded-xl border p-4 shadow-sm ${toneCls}`}>
      <div className="flex items-start gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconCls}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
          <p className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900">{value}</p>
          {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

function Panel({
  title,
  action,
  children,
  className = '',
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-xl border border-gray-100 bg-white p-4 shadow-sm ${className}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function HealthBadge({
  level,
  label,
  score,
}: {
  level: 'healthy' | 'watch' | 'critical';
  label: string;
  score: number;
}) {
  const cls =
    level === 'healthy'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
      : level === 'watch'
        ? 'border-amber-200 bg-amber-50 text-amber-950'
        : 'border-red-200 bg-red-50 text-red-950';
  const Icon = level === 'healthy' ? ShieldCheck : level === 'watch' ? Activity : AlertTriangle;
  return (
    <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${cls}`}>
      <Icon className="h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">
          Media health: {label}{' '}
          <span className="tabular-nums opacity-80">({score}/100)</span>
        </p>
        <p className="text-xs opacity-80">
          Composite of flagged volume, inbox backlog, negative share, and urgency.
        </p>
      </div>
    </div>
  );
}

const MediaOverviewPage = () => {
  const [range, setRange] = useState<RangeKey>('30d');
  const days = rangeDays(range);
  const windowParams = useMemo(() => publishedRangeParams(days), [days]);
  const { items } = useCoverageItems(windowParams);
  const accounts = useAdaptedAccounts();
  const outlets = useAdaptedOutlets();
  const { keywords } = useApiTrackedKeywords();
  const listeningRules = useListeningRulesQuery({ limit: 200 });
  const briefsQ = useBriefsQuery({ limit: 100, status: 'draft' });
  const trackedPerf = usePerformanceTrackedQuery({
    range_days: days === 'all' ? 90 : days,
  });
  const activeRules = (listeningRules.data?.items ?? []).filter((r) => r.enabled);

  // Server already applied from/to when set; keep items as the scoped set.
  const scoped = items;

  const active = useMemo(
    () => scoped.filter((i) => i.status !== 'archived'),
    [scoped],
  );

  const unreviewed = active.filter((i) => i.status === 'new').length;
  const flagged = active.filter((i) => i.status === 'flagged').length;
  const social = scoped.filter((i) => i.channel === 'social');
  const traditional = scoped.filter((i) => i.channel === 'traditional');
  const negative = active.filter((i) => i.sentiment === 'negative');
  const positive = active.filter((i) => i.sentiment === 'positive');

  const totalReach = scoped.reduce((s, i) => {
    const m = publicMetrics(i);
    return s + (m.reach ?? m.views ?? 0);
  }, 0);
  const totalPrValue = scoped.reduce((s, i) => {
    const m = publicMetrics(i);
    return s + (m.prValue ?? 0);
  }, 0);
  const engRates = scoped
    .map((i) => publicMetrics(i).engagementRate)
    .filter((n): n is number => n != null);
  const avgEng = avg(engRates);
  const avgUrgency = avg(
    active.map((i) => i.urgencyScore).filter((n): n is number => n != null),
  );
  const negativeShare = active.length ? negative.length / active.length : 0;
  const positiveShare = active.length ? positive.length / active.length : 0;

  const health = healthFromSignals({
    flagged,
    unreviewed,
    negativeShare,
    avgUrgency,
  });

  const sentimentMix = (['positive', 'neutral', 'negative', 'unknown'] as const)
    .map((s) => ({
      name: s,
      value: scoped.filter((i) => i.sentiment === s).length,
      fill: SENTIMENT_COLORS[s],
    }))
    .filter((d) => d.value > 0);

  const classMix = (['owned', 'earned', 'shared', 'paid'] as const)
    .map((c) => ({
      name: c,
      value: scoped.filter((i) => i.mediaClass === c).length,
    }))
    .filter((d) => d.value > 0);

  const channelMix = [
    { name: 'Social', value: social.length, fill: CHANNEL_COLORS.social },
    { name: 'Traditional', value: traditional.length, fill: CHANNEL_COLORS.traditional },
  ].filter((d) => d.value > 0);

  const volumeSeries = useMemo(() => {
    const n = days === 'all' ? 90 : days;
    const startMs = Date.now() - (n - 1) * 86_400_000;
    const map = new Map<string, { date: string; social: number; traditional: number; negative: number }>();
    for (let i = 0; i < n; i++) {
      const d = new Date(startMs + i * 86_400_000);
      const key = d.toISOString().slice(0, 10);
      map.set(key, { date: key.slice(5), social: 0, traditional: 0, negative: 0 });
    }
    for (const item of scoped) {
      const key = item.publishedAt.slice(0, 10);
      const row = map.get(key);
      if (!row) continue;
      if (item.channel === 'social') row.social += 1;
      else row.traditional += 1;
      if (item.sentiment === 'negative') row.negative += 1;
    }
    return [...map.values()];
  }, [scoped, days]);

  const platformBars = useMemo(() => {
    const counts: Partial<Record<SocialPlatform, number>> = {};
    for (const item of social) {
      if (!item.platform) continue;
      counts[item.platform] = (counts[item.platform] ?? 0) + 1;
    }
    return Object.entries(counts)
      .map(([platform, count]) => ({
        platform: platform as SocialPlatform,
        label: PLATFORM_LABEL[platform as SocialPlatform],
        count: count ?? 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [social]);

  const mediumBars = useMemo(() => {
    const counts: Partial<Record<TraditionalMedium, number>> = {};
    for (const item of traditional) {
      const outlet = outlets.find((o) => o.id === item.outletId);
      const medium = outlet?.medium ?? 'other';
      counts[medium] = (counts[medium] ?? 0) + 1;
    }
    return Object.entries(counts)
      .map(([medium, count]) => ({
        medium: medium as TraditionalMedium,
        label: MEDIUM_LABEL[medium as TraditionalMedium],
        count: count ?? 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [traditional, outlets]);

  const attentionQueue = useMemo(
    () =>
      [...active]
        .filter((i) => i.status === 'flagged' || i.status === 'new' || i.sentiment === 'negative')
        .sort((a, b) => (b.urgencyScore ?? -1) - (a.urgencyScore ?? -1))
        .slice(0, 8),
    [active],
  );

  const topSources = useMemo(() => {
    type Row = {
      key: string;
      label: string;
      kind: 'account' | 'outlet';
      platform?: SocialPlatform;
      website?: string;
      reach: number;
      count: number;
    };
    const map = new Map<string, Row>();

    for (const item of scoped) {
      const m = publicMetrics(item);
      const reach = m.reach ?? m.views ?? 0;
      if (item.channel === 'social' && item.social) {
        const acc = accounts.find((a) => a.id === item.social?.accountId);
        const key = acc?.id ?? `h:${item.social.authorHandle}`;
        const existing = map.get(key) ?? {
          key,
          label: acc?.displayName ?? item.social.authorDisplayName,
          kind: 'account' as const,
          platform: item.platform,
          reach: 0,
          count: 0,
        };
        existing.reach += reach;
        existing.count += 1;
        map.set(key, existing);
      } else if (item.outletId || item.outletName) {
        const out = outlets.find((o) => o.id === item.outletId);
        const key = out?.id ?? `o:${item.outletName}`;
        const existing = map.get(key) ?? {
          key,
          label: out?.name ?? item.outletName ?? 'Outlet',
          kind: 'outlet' as const,
          website: out?.website,
          reach: 0,
          count: 0,
        };
        existing.reach += reach;
        existing.count += 1;
        map.set(key, existing);
      }
    }

    return [...map.values()].sort((a, b) => b.reach - a.reach).slice(0, 6);
  }, [scoped, accounts, outlets]);

  const keywordPerf = useMemo(() => {
    const relevant = keywords.filter((k) => k.enabled);
    return relevant
      .map((kw) => {
        const hits = scoped.filter((item) => matchingKeywords(item, [kw]).length > 0);
        return {
          kw,
          hits: hits.length,
          negative: hits.filter((h) => h.sentiment === 'negative').length,
          reach: hits.reduce((s, h) => {
            const m = publicMetrics(h);
            return s + (m.reach ?? m.views ?? 0);
          }, 0),
        };
      })
      .sort((a, b) => b.hits - a.hits)
      .slice(0, 8);
  }, [keywords, scoped]);

  const ownedAccounts = accounts.filter((a) => a.owned);
  const ownedQuiet = ownedAccounts.filter((a) => {
    const last = scoped.find(
      (i) => i.social?.accountId === a.id || (i.platform === a.platform && i.kind === 'post'),
    );
    return !last;
  });

  const tier1Outlets = outlets.filter((o) => o.tier === 1);
  const tier1WithCoverage = tier1Outlets.filter((o) =>
    scoped.some((i) => i.outletId === o.id),
  );

  const briefsDraft = briefsQ.data?.total ?? briefsQ.data?.count ?? 0;
  const sov = trackedPerf.data?.share_of_voice ?? [];

  const rangeLabel =
    range === '7d'
      ? 'Last 7 days'
      : range === '30d'
        ? 'Last 30 days'
        : range === '90d'
          ? 'Last 90 days'
          : 'All time (page capped)';

  return (
    <MediaPageShell
      title="Media dashboard"
      description="Cross-channel health of coverage, sentiment, sources, and listening — what needs action now."
      crumbs={[
        { label: 'Radar', href: '/admin/marketing/radar/performance' },
        { label: 'Overview' },
      ]}
      icon={Newspaper}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border border-gray-200 bg-white p-0.5">
            {([
              ['7d', '7d'],
              ['30d', '30d'],
              ['90d', '90d'],
              ['all', 'All'],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setRange(key)}
                className={`rounded px-2.5 py-1.5 text-xs font-medium ${
                  range === key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <Button asChild className="gap-2">
            <Link to="/admin/marketing/radar/inbox">
              <Inbox className="h-4 w-4" /> Open inbox
            </Link>
          </Button>
          <Button asChild variant="outline" className="gap-2">
            <Link to="/admin/marketing/radar/performance">
              <Activity className="h-4 w-4" /> Performance
            </Link>
          </Button>
        </div>
      }
    >
      <p className="mb-4 text-sm text-gray-600">
        Operational coverage health lives here. For roll-up marketing performance, see{' '}
        <Link to="/admin/marketing/radar/performance" className="font-medium text-blue-700 hover:underline">
          Outflows → Performance
        </Link>
        {' '}or{' '}
        <Link to="/admin/marketing/radar/performance?tab=sentiment" className="font-medium text-blue-700 hover:underline">
          Sentiment
        </Link>
        . Comparative reach uses public-surface metrics only.
      </p>
      <div className="mb-4">
        <HealthBadge level={health.level} label={health.label} score={health.score} />
      </div>
      <div className="mb-4">
        <ListeningHealthStrip />
      </div>

      {(flagged > 0 || negative.length > 0) && (
        <div className="mb-4 flex flex-wrap items-start gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">Attention required</p>
            <p className="mt-0.5 text-red-800">
              {flagged} flagged · {negative.length} negative · {unreviewed} unreviewed in {rangeLabel.toLowerCase()}.
              Triage inbox by urgency, then update briefs if leadership needs a digest.
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild size="sm" variant="outline" className="border-red-200 bg-white">
              <Link to="/admin/marketing/radar/inbox">Triage</Link>
            </Button>
            <Button asChild size="sm" variant="outline" className="border-red-200 bg-white">
              <Link to="/admin/marketing/radar/briefs">Briefs</Link>
            </Button>
          </div>
        </div>
      )}

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          label="Coverage volume"
          value={String(scoped.length)}
          hint={`${social.length} social · ${traditional.length} traditional`}
          icon={Activity}
        />
        <Kpi
          label="Combined reach"
          value={formatCompact(totalReach)}
          hint={`PR value $${formatCompact(totalPrValue)}`}
          icon={Eye}
        />
        <Kpi
          label="Avg engagement"
          value={engRates.length ? `${(avgEng * 100).toFixed(1)}%` : '—'}
          hint={`${engRates.length} items with ER`}
          icon={Heart}
          tone={avgEng >= 0.05 ? 'good' : 'default'}
        />
        <Kpi
          label="Sentiment"
          value={`${Math.round(positiveShare * 100)}% pos`}
          hint={`${Math.round(negativeShare * 100)}% negative`}
          icon={positiveShare >= 0.5 ? TrendingUp : negativeShare >= 0.25 ? TrendingDown : Minus}
          tone={negativeShare >= 0.25 ? 'danger' : positiveShare >= 0.5 ? 'good' : 'default'}
        />
        <Kpi
          label="Inbox backlog"
          value={String(unreviewed)}
          hint="Status = new"
          icon={Inbox}
          tone={unreviewed >= 5 ? 'warn' : unreviewed > 0 ? 'default' : 'good'}
        />
        <Kpi
          label="Flagged / crisis"
          value={String(flagged)}
          hint={
            Number.isFinite(avgUrgency)
              ? `Avg urgency ${avgUrgency.toFixed(0)}`
              : 'Urgency unscored on discover'
          }
          icon={Flag}
          tone={flagged > 0 ? 'danger' : 'good'}
        />
        <Kpi
          label="Active trackers"
          value={String(activeRules.length)}
          hint={`${keywords.filter((k) => k.enabled).length} keywords · ${keywordPerf.reduce((s, k) => s + k.hits, 0)} hits in range`}
          icon={Radio}
        />
        <Kpi
          label="Sources"
          value={String(accounts.length + outlets.length)}
          hint={`${ownedAccounts.length} owned · ${tier1Outlets.length} tier-1 outlets`}
          icon={Building2}
        />
      </div>

      {sov.length > 0 && (
        <div className="mb-6 grid gap-3 sm:grid-cols-3">
          {sov.map((row) => (
            <div
              key={row.subject}
              className="rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                SoV · {row.subject}
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
                {row.share != null ? `${(row.share * 100).toFixed(0)}%` : '—'}
              </p>
              <p className="text-xs text-gray-400">
                {row.posts} posts · {formatCompact(row.engagement)} public eng
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="mb-6 grid gap-4 xl:grid-cols-3">
        <Panel
          title="Coverage volume"
          className="xl:col-span-2"
          action={<span className="text-[11px] text-gray-400">{rangeLabel}</span>}
        >
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={volumeSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
                <Tooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="social"
                  stackId="1"
                  stroke={CHANNEL_COLORS.social}
                  fill={CHANNEL_COLORS.social}
                  fillOpacity={0.35}
                  name="Social"
                />
                <Area
                  type="monotone"
                  dataKey="traditional"
                  stackId="1"
                  stroke={CHANNEL_COLORS.traditional}
                  fill={CHANNEL_COLORS.traditional}
                  fillOpacity={0.35}
                  name="Traditional"
                />
                <Area
                  type="monotone"
                  dataKey="negative"
                  stroke="#e11d48"
                  fill="transparent"
                  strokeWidth={2}
                  name="Negative"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Sentiment mix">
          <div className="h-56">
            {sentimentMix.length === 0 ? (
              <p className="flex h-full items-center justify-center text-sm text-gray-500">No data</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sentimentMix}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={48}
                    outerRadius={78}
                    paddingAngle={2}
                  >
                    {sentimentMix.map((d) => (
                      <Cell key={d.name} fill={d.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Panel>
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-3">
        <Panel title="Channel mix">
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={channelMix} dataKey="value" nameKey="name" outerRadius={70}>
                  {channelMix.map((d) => (
                    <Cell key={d.name} fill={d.fill} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="PESO / media class">
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={classMix} layout="vertical" margin={{ left: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={64} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#0f172a" radius={[0, 4, 4, 0]} name="Items" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Social platforms">
          {platformBars.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">No social items in range</p>
          ) : (
            <ul className="space-y-2">
              {platformBars.map((row) => (
                <li key={row.platform} className="flex items-center gap-2 text-sm">
                  <PlatformLogoBadge platform={row.platform} size="sm" />
                  <span className="flex-1 font-medium text-gray-800">{row.label}</span>
                  <span className="tabular-nums text-gray-600">{row.count}</span>
                  <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-blue-600"
                      style={{
                        width: `${Math.max(8, (row.count / (platformBars[0]?.count || 1)) * 100)}%`,
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <Panel
          title="Attention queue"
          action={
            <Link to="/admin/marketing/radar/inbox" className="text-xs font-medium text-blue-700 hover:underline">
              Inbox →
            </Link>
          }
        >
          {attentionQueue.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">Nothing urgent in this range.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {attentionQueue.map((item) => (
                <AttentionRow key={item.id} item={item} />
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Top sources by reach"
          action={
            <div className="flex gap-2 text-xs">
              <Link to="/admin/marketing/sources/accounts" className="font-medium text-blue-700 hover:underline">
                Accounts
              </Link>
              <Link to="/admin/marketing/sources/outlets" className="font-medium text-blue-700 hover:underline">
                Outlets
              </Link>
            </div>
          }
        >
          <ul className="space-y-2">
            {topSources.map((row) => (
              <li
                key={row.key}
                className="flex items-center gap-3 rounded-lg border border-gray-50 px-2 py-2 hover:bg-gray-50"
              >
                {row.kind === 'account' && row.platform ? (
                  <PlatformLogoBadge platform={row.platform} size="sm" />
                ) : (
                  <OutletFavicon name={row.label} website={row.website} size="sm" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">{row.label}</p>
                  <p className="text-[11px] text-gray-500">
                    {row.kind} · {row.count} item{row.count === 1 ? '' : 's'}
                  </p>
                </div>
                <span className="text-sm font-semibold tabular-nums text-gray-800">
                  {formatCompact(row.reach)}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <Panel
          title="Keyword / hashtag trackers"
          action={
            <Link
              to="/admin/marketing/radar/inbox?tab=mentions"
              className="text-xs font-medium text-blue-700 hover:underline"
            >
              Manage →
            </Link>
          }
        >
          {keywordPerf.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No enabled trackers.</p>
          ) : (
            <ul className="space-y-2">
              {keywordPerf.map(({ kw, hits, negative: neg, reach }) => (
                <li key={kw.id} className="flex items-center gap-3 text-sm">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-100 text-violet-700">
                    <Zap className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900">{kw.label}</p>
                    <p className="text-[11px] text-gray-500">
                      {kw.channels.join(' / ')} · {kw.matchMode.toUpperCase()}
                      {neg > 0 ? ` · ${neg} negative` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold tabular-nums text-gray-900">{hits}</p>
                    <p className="text-[11px] text-gray-500">{formatCompact(reach)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Source health">
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                <p className="text-[11px] uppercase tracking-wide text-gray-500">Owned accounts</p>
                <p className="mt-1 text-lg font-semibold tabular-nums">
                  {ownedAccounts.length - ownedQuiet.length}/{ownedAccounts.length}
                </p>
                <p className="text-xs text-gray-500">active in range</p>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                <p className="text-[11px] uppercase tracking-wide text-gray-500">Tier-1 outlets</p>
                <p className="mt-1 text-lg font-semibold tabular-nums">
                  {tier1WithCoverage.length}/{tier1Outlets.length}
                </p>
                <p className="text-xs text-gray-500">with coverage</p>
              </div>
            </div>
            {ownedQuiet.length > 0 && (
              <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-amber-950">
                <p className="font-medium">Quiet owned accounts</p>
                <p className="mt-0.5 text-xs">
                  {ownedQuiet.map((a) => a.handle).join(', ')} — no posts/mentions in {rangeLabel.toLowerCase()}.
                </p>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {mediumBars.map((m) => (
                <span
                  key={m.medium}
                  className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-800"
                >
                  {m.label} · {m.count}
                </span>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              {briefsDraft} draft brief{briefsDraft === 1 ? '' : 's'} ready for review.
            </p>
          </div>
        </Panel>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Inbox', href: '/admin/marketing/radar/inbox', icon: Inbox },
          { label: 'Social posts', href: '/admin/marketing/radar/coverage/social', icon: Share2 },
          { label: 'Social mentions', href: '/admin/marketing/radar/inbox?tab=mentions', icon: Radio },
          { label: 'Publications', href: '/admin/marketing/radar/coverage/traditional', icon: BookOpen },
          { label: 'Clips', href: '/admin/marketing/radar/coverage/traditional', icon: Activity },
          { label: 'Accounts', href: '/admin/marketing/sources/accounts', icon: Users },
          { label: 'Outlets', href: '/admin/marketing/sources/outlets', icon: Building2 },
          { label: 'Briefs', href: '/admin/marketing/radar/briefs', icon: FileStack },
        ].map((link) => (
          <Link
            key={link.label}
            to={link.href}
            className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm font-medium text-gray-800 shadow-sm transition hover:border-blue-200 hover:bg-blue-50"
          >
            <link.icon className="h-4 w-4 text-blue-700" />
            {link.label} →
          </Link>
        ))}
      </div>
    </MediaPageShell>
  );
};

function AttentionRow({ item }: { item: CoverageItem }) {
  const m = publicMetrics(item);
  return (
    <li className="flex items-start justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-900">{item.title}</p>
        <p className="mt-0.5 text-xs text-gray-500">
          {sourceLabel(item)} · urg {item.urgencyScore ?? '—'} ·{' '}
          {formatCompact(m.reach ?? m.views)}
        </p>
        <div className="mt-1 flex flex-wrap gap-1">
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${MEDIA_STATUS_BADGE[item.status]}`}
          >
            {item.status}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${MEDIA_SENTIMENT_BADGE[item.sentiment]}`}
          >
            {item.sentiment}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${MEDIA_CLASS_BADGE[item.mediaClass]}`}
          >
            {item.mediaClass}
          </span>
        </div>
      </div>
      <Link
        to="/admin/marketing/radar/inbox"
        className="shrink-0 text-xs font-medium text-blue-700 hover:underline"
      >
        Open
      </Link>
    </li>
  );
}

export default MediaOverviewPage;
